<template>
  <div class="book-earnings" v-loading="loading">
    <h2>作者收益</h2>

    <!-- 筛选 -->
    <el-card class="block" shadow="never">
      <div class="filter-content">
        <div class="filter-item">
          <label>搜索：</label>
          <el-input
            v-model="keyword"
            placeholder="书名或作者名"
            clearable
            style="width: 260px"
            @clear="load"
            @keyup.enter.native="load"
          />
        </div>
        <el-button type="primary" icon="el-icon-search" @click="load">查询</el-button>
        <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>

        <span class="spacer"></span>
        <span class="totals">
          共 <b>{{ list.length }}</b> 本 ·
          {{ thisMonth.label }} <b class="money">¥{{ formatMoney(thisMonthTotal) }}</b> ·
          {{ lastMonth.label }} <b class="money">¥{{ formatMoney(lastMonthTotal) }}</b>
        </span>
      </div>

      <div class="filter-tip">
        稿费 = 该账期结算字数 × 该书千字单价 ÷ 1000。<strong>千字单价带 * 号的表示尚未设定，按 {{ defaultPrice }} 元兜底计算。</strong><br />
        账期自 2026-07 起为自然月；此前为「上月10日~本月9日」，2026-06-10~06-30 为过渡周期 —— 展开行里会看到跨月的账期名。<br />
        <strong>点击一行可展开</strong>，看这本书历史上每个账期的稿费，以及该账期结算了哪几章。点「本月稿费」「上月稿费」列头可切换排序，默认按上月稿费降序。<br />
        列出的是<strong>有结算记录或已登记作者</strong>的书：已签约但还没结算的显示 ¥0，有字数但漏配作者的会标出「未配作者」。
      </div>
    </el-card>

    <el-table
      v-if="list.length > 0"
      ref="tbl"
      :data="list"
      stripe
      border
      row-key="game_id"
      :default-sort="{ prop: 'last_earnings', order: 'descending' }"
      @row-click="onRowClick"
      @expand-change="onExpandChange"
    >
      <!-- 展开：该书的全部历史账期与章节明细 -->
      <el-table-column type="expand">
        <template slot-scope="{ row }">
          <div class="detail" v-loading="detailLoading[row.game_id]">
            <div v-if="detailError[row.game_id]" class="detail-error">
              <span>{{ detailError[row.game_id] }}</span>
              <el-button type="text" @click="loadDetail(row.game_id, true)">重试</el-button>
            </div>

            <template v-else-if="detail[row.game_id]">
              <div v-if="detail[row.game_id].periods.length === 0" class="empty-tip">
                这本书还没有任何结算记录
              </div>
              <el-collapse v-else v-model="openedPeriods[row.game_id]">
                <el-collapse-item
                  v-for="p in detail[row.game_id].periods"
                  :key="p.bill_start"
                  :name="p.bill_start"
                >
                  <template slot="title">
                    <div class="period-header">
                      <span class="p-label">{{ p.label }}</span>
                      <span class="p-range">{{ p.bill_start }} ~ {{ p.bill_end }}</span>
                      <span class="p-meta">{{ formatNumber(p.total_words) }} 字</span>
                      <span class="p-meta">{{ p.chapters.length }} 章</span>
                      <span class="p-meta money">¥{{ formatMoney(p.total_earnings) }}</span>
                    </div>
                  </template>

                  <el-table :data="p.chapters" size="mini" border>
                    <el-table-column label="章节" min-width="240">
                      <template slot-scope="c">{{ c.row.chapter_title }}</template>
                    </el-table-column>
                    <el-table-column label="字数" width="120" align="right">
                      <template slot-scope="c">{{ formatNumber(c.row.word_count) }}</template>
                    </el-table-column>
                    <el-table-column label="稿费" width="130" align="right">
                      <template slot-scope="c">¥{{ formatMoney(c.row.earnings) }}</template>
                    </el-table-column>
                  </el-table>
                </el-collapse-item>
              </el-collapse>
            </template>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="title" label="小说" min-width="220" show-overflow-tooltip>
        <template slot-scope="{ row }">
          <span>{{ row.title }}</span>
          <span class="dim tiny">#{{ row.game_id }}</span>
        </template>
      </el-table-column>

      <el-table-column label="作者" min-width="190">
        <template slot-scope="{ row }">
          <span v-if="!row.authors || row.authors.length === 0" class="no-author">未配作者</span>
          <template v-else>
            <span v-for="(a, i) in row.authors" :key="a.author_id">
              <span v-if="i > 0">、</span>
              <a class="author-link" @click.stop="openProfile(a.author_id)">{{ a.name }}</a>
              <!-- 资料没录就标出来：这本书要打款但没卡号，属于必须先处理的事 -->
              <el-tooltip v-if="!a.profile_filled" content="打款资料未录入，点击录入" placement="top">
                <i class="el-icon-warning no-profile"></i>
              </el-tooltip>
            </span>
          </template>
        </template>
      </el-table-column>

      <el-table-column label="笔名" min-width="130">
        <template slot-scope="{ row }">
          <span v-if="!row.pen_name" class="dim">-</span>
          <span v-else>{{ row.pen_name }}</span>
        </template>
      </el-table-column>

      <el-table-column label="千字单价" width="110" align="right">
        <template slot-scope="{ row }">
          <span :class="{ 'price-default': row.price_is_default }">
            ¥{{ row.price_per_thousand_words }}<span v-if="row.price_is_default">*</span>
          </span>
        </template>
      </el-table-column>

      <el-table-column
        prop="this_earnings"
        :label="`本月稿费（${thisMonth.label}）`"
        width="180"
        align="right"
        sortable
      >
        <template slot-scope="{ row }">
          <span v-if="!row.this_earnings" class="dim">-</span>
          <el-tooltip v-else placement="top">
            <div slot="content">
              {{ formatNumber(row.this_month.words) }} 字 × ¥{{ row.price_per_thousand_words }}/千字
            </div>
            <span class="money">¥{{ formatMoney(row.this_earnings) }}</span>
          </el-tooltip>
        </template>
      </el-table-column>

      <el-table-column
        prop="last_earnings"
        :label="`上月稿费（${lastMonth.label}）`"
        width="180"
        align="right"
        sortable
      >
        <template slot-scope="{ row }">
          <span v-if="!row.last_earnings" class="dim">-</span>
          <el-tooltip v-else placement="top">
            <div slot="content">
              {{ formatNumber(row.last_month.words) }} 字 × ¥{{ row.price_per_thousand_words }}/千字
            </div>
            <span class="money">¥{{ formatMoney(row.last_earnings) }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="!loading && list.length === 0" class="empty">
      <i class="el-icon-wallet"></i>
      <p>{{ keyword ? '没有匹配的小说或作者' : '暂无收益数据' }}</p>
    </div>

    <!-- 作者打款信息 -->
    <el-card class="block" shadow="never">
      <div slot="header" class="card-header">
        <span>作者信息（打款用）</span>
        <span class="hint">
          共 {{ profiles.length }} 位作者，已录入 {{ filledCount }} 位
          <b v-if="profiles.length - filledCount > 0" class="warn">，还差 {{ profiles.length - filledCount }} 位</b>
          · {{ thisMonth.label }}应付 <b class="money">¥{{ formatMoney(profileCurTotal) }}</b>
          · {{ lastMonth.label }}应付 <b class="money">¥{{ formatMoney(profileLastTotal) }}</b>
        </span>
        <span class="spacer"></span>
        <el-button type="text" size="mini" :loading="profileLoading" @click="toggleReveal">
          {{ revealed ? '隐藏完整号码' : '显示完整号码' }}
        </el-button>
        <el-button type="text" size="mini" @click="loadProfiles()">刷新</el-button>
      </div>

      <!-- 数据错误报警：一本书挂多个作者 -->
      <el-alert
        v-for="b in multiAuthorBooks" :key="'ma-' + b.book_id"
        type="error" :closable="false" show-icon class="pii-tip"
      >
        <template slot="title">
          《{{ b.title }}》挂了 <b>{{ b.authors.length }}</b> 个作者（{{ b.authors.join('、') }}）——
          <b>一本书应当只有一个作者</b>。这本书上月稿费 ¥{{ formatMoney(b.last_month) }} 已<b>完整计入每个人</b>名下，
          下表「上月应付」合计因此虚增 <b>¥{{ formatMoney(b.overcounted) }}</b>。请先修正该书的作者配置。
        </template>
      </el-alert>

      <el-alert
        v-if="orphanBooks.length > 0"
        type="warning" :closable="false" show-icon class="pii-tip"
      >
        <template slot="title">
          有 {{ orphanBooks.length }} 条作者关联指向了<b>已删除的小说</b>（book_id
          {{ orphanBooks.map(o => o.book_id).join('、') }}），不产生稿费，建议清理。
        </template>
      </el-alert>

      <el-alert type="info" :closable="false" show-icon class="pii-tip">
        <template slot="title">
          身份证号、银行卡号仅<b>管理员</b>可见，列表默认脱敏。
          点「显示完整号码」或打开编辑框会在服务端<b>记录一条日志</b>（谁、什么时候、看了谁的资料）。
        </template>
      </el-alert>

      <el-table :data="profiles" size="small" border empty-text="还没有任何挂书的作者">
        <el-table-column prop="username" label="后台账号" min-width="130" />
        <el-table-column prop="book_count" label="作品数" width="80" align="center" />
        <el-table-column label="姓名" width="110">
          <template slot-scope="{ row }">
            <span v-if="!row.real_name" class="dim">-</span>
            <span v-else>{{ row.real_name }}</span>
          </template>
        </el-table-column>
        <el-table-column label="身份证" min-width="190">
          <template slot-scope="{ row }">
            <span v-if="!row.id_card" class="dim">-</span>
            <span v-else class="mono">{{ row.id_card }}</span>
          </template>
        </el-table-column>
        <el-table-column label="手机号" width="140">
          <template slot-scope="{ row }">
            <span v-if="!row.phone" class="dim">-</span>
            <span v-else class="mono">{{ row.phone }}</span>
          </template>
        </el-table-column>
        <el-table-column label="银行卡号" min-width="190">
          <template slot-scope="{ row }">
            <span v-if="!row.bank_card" class="dim">-</span>
            <span v-else class="mono">{{ row.bank_card }}</span>
          </template>
        </el-table-column>
        <el-table-column label="开户行" min-width="160">
          <template slot-scope="{ row }">
            <span v-if="!row.bank_name" class="dim">-</span>
            <span v-else>{{ row.bank_name }}</span>
          </template>
        </el-table-column>
        <el-table-column label="星果ID" width="130">
          <template slot-scope="{ row }">
            <span v-if="!row.xingguo_id" class="dim">-</span>
            <span v-else class="mono">{{ row.xingguo_id }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="`本月应付（${thisMonth.label}）`" width="150" align="right" sortable
          :sort-method="(a, b) => a.this_month_earnings - b.this_month_earnings">
          <template slot-scope="{ row }">
            <span v-if="!row.this_month_earnings" class="dim">-</span>
            <span v-else class="money">¥{{ formatMoney(row.this_month_earnings) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="`上月应付（${lastMonth.label}）`" width="150" align="right" sortable
          :sort-method="(a, b) => a.last_month_earnings - b.last_month_earnings">
          <template slot-scope="{ row }">
            <span v-if="!row.last_month_earnings" class="dim">-</span>
            <span v-else class="money">¥{{ formatMoney(row.last_month_earnings) }}</span>
            <el-tooltip v-if="row.has_multi_author_book" placement="top"
              content="名下有书挂了多个作者，该书稿费被完整计入每人，此金额偏高">
              <i class="el-icon-warning multi-warn"></i>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.has_data" size="mini" type="success">已录入</el-tag>
            <el-tag v-else size="mini" type="danger">未录入</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="90" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="openProfile(row.author_id)">
              {{ row.has_data ? '编辑' : '录入' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 录入/编辑（与【稿费结算】页共用同一个组件） -->
    <author-profile-dialog
      :visible.sync="profileDialog"
      :author-id="profileAuthorId"
      :author-name="profileAuthorName"
      @saved="onProfileSaved"
    />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import {
  getBookEarnings, getBookEarningsDetail,
  getAuthorProfiles,
  BookEarningsRow, BookEarningsMonthMeta, BookEarningsDetail, AuthorProfileRow,
  MultiAuthorBookWarn, OrphanBookWarn,
} from '@/api/earnings'
import AuthorProfileDialog from '@/components/AuthorProfileDialog.vue'

// 与后端 defaultPricePerThousandWords 对应，仅用于文案说明
const DEFAULT_PRICE = 80

// 表格行：在接口数据上补两个扁平字段供 el-table 排序用。
// el-table 的 sortable 只认顶层 prop，写 "this_month.earnings" 排不动。
type Row = BookEarningsRow & { this_earnings: number; last_earnings: number }

const EMPTY_MONTH: BookEarningsMonthMeta = { bill_start: '', bill_end: '', label: '本月' }

@Component({ components: { AuthorProfileDialog } })
export default class AuthorEarnings extends Vue {
  // 吸收 router-view 透传的 prop，本页不使用
  @Prop() readonly game?: unknown
  @Prop() readonly games2?: unknown

  defaultPrice = DEFAULT_PRICE

  loading = false
  keyword = ''
  list: Row[] = []
  thisMonth: BookEarningsMonthMeta = { ...EMPTY_MONTH }
  lastMonth: BookEarningsMonthMeta = { ...EMPTY_MONTH, label: '上月' }
  thisMonthTotal = 0
  lastMonthTotal = 0

  // 展开行的懒加载状态，key 为 game_id
  detail: Record<number, BookEarningsDetail> = {}
  detailLoading: Record<number, boolean> = {}
  detailError: Record<number, string> = {}
  // 每本书各自记住展开了哪些账期，互不影响
  openedPeriods: Record<number, string[]> = {}

  // ── 作者打款信息 ──
  profiles: AuthorProfileRow[] = []
  profileCurTotal = 0
  profileLastTotal = 0
  // 数据错误：一本书挂多个作者 / 关联指向已删除的书
  multiAuthorBooks: MultiAuthorBookWarn[] = []
  orphanBooks: OrphanBookWarn[] = []
  profileLoading = false
  // 是否已请求过明文。切到明文会在服务端留痕，所以不做默认、必须显式点
  revealed = false
  profileDialog = false
  profileAuthorId = 0
  profileAuthorName = ''

  mounted() {
    this.load()
    this.loadProfiles()
  }

  get filledCount(): number {
    return this.profiles.filter(p => p.has_data).length
  }

  async loadProfiles(reveal = this.revealed) {
    this.profileLoading = true
    try {
      const res = await getAuthorProfiles('', reveal)
      this.profiles = res.list || []
      this.revealed = !res.masked
      this.profileCurTotal = res.this_month_total || 0
      this.profileLastTotal = res.last_month_total || 0
      this.multiAuthorBooks = res.multi_author_books || []
      this.orphanBooks = res.orphan_books || []
    } catch (e: any) {
      this.$message.error(e?.response?.data?.message || e?.message || '获取作者信息失败')
    } finally {
      this.profileLoading = false
    }
  }

  async toggleReveal() {
    if (this.revealed) {
      await this.loadProfiles(false)
      return
    }
    // 看明文会在服务端记一条日志，点之前先说清楚
    try {
      await this.$confirm(
        '将显示所有作者的<b>完整身份证号与银行卡号</b>。<br><br>' +
        '此操作会在服务端记录一条日志（操作人、时间）。确定吗？',
        '显示完整号码',
        { dangerouslyUseHTMLString: true, confirmButtonText: '确定显示', cancelButtonText: '取消', type: 'warning' }
      )
    } catch {
      return
    }
    await this.loadProfiles(true)
  }

  // 从书表的作者名或作者信息表的按钮进来，都走这里；表单与回填逻辑在共用组件里
  openProfile(authorId: number) {
    const brief = this.profiles.find(p => p.author_id === authorId)
    this.profileAuthorId = authorId
    this.profileAuthorName = brief?.username || ''
    this.profileDialog = true
  }

  async onProfileSaved() {
    await this.loadProfiles()
    await this.load() // 书表里的「未录入」标记要跟着更新
  }

  async load() {
    this.loading = true
    try {
      const res = await getBookEarnings(this.keyword.trim())
      this.list = (res.list || []).map(r => ({
        ...r,
        this_earnings: r.this_month.earnings,
        last_earnings: r.last_month.earnings,
      }))
      this.thisMonth = res.this_month || { ...EMPTY_MONTH }
      this.lastMonth = res.last_month || { ...EMPTY_MONTH, label: '上月' }
      this.thisMonthTotal = res.this_month_total || 0
      this.lastMonthTotal = res.last_month_total || 0
      // 重新查询后旧的明细已不对应，清掉避免展开时看到上一次的书
      this.detail = {}
      this.detailError = {}
      this.openedPeriods = {}
    } catch (e: any) {
      this.$message.error(e?.response?.data?.message || e?.message || '获取收益数据失败')
    } finally {
      this.loading = false
    }
  }

  handleReset() {
    this.keyword = ''
    this.load()
  }

  // 点整行也能展开，不必非得点那个小箭头
  onRowClick(row: Row) {
    (this.$refs.tbl as any)?.toggleRowExpansion(row)
  }

  onExpandChange(row: Row, expanded: Row[] | boolean) {
    // el-table 在有 row-key 时回传数组，无 row-key 时回传布尔，两种都兼容
    const isOpen = Array.isArray(expanded)
      ? expanded.some(r => r.game_id === row.game_id)
      : !!expanded
    if (isOpen) this.loadDetail(row.game_id)
  }

  async loadDetail(gameId: number, force = false) {
    if (!force && this.detail[gameId]) return
    this.$set(this.detailLoading, gameId, true)
    this.$set(this.detailError, gameId, '')
    try {
      const d = await getBookEarningsDetail(gameId)
      this.$set(this.detail, gameId, d)
      // 默认展开最新账期：打开就能看到最近结算了哪几章
      if (d.periods.length > 0 && !this.openedPeriods[gameId]) {
        this.$set(this.openedPeriods, gameId, [d.periods[0].bill_start])
      }
    } catch (e: any) {
      this.$set(this.detailError, gameId, e?.message || '加载失败，请重试')
    } finally {
      this.$set(this.detailLoading, gameId, false)
    }
  }

  formatNumber(num: number): string {
    if (num == null) return '0'
    return num.toLocaleString('zh-CN')
  }

  formatMoney(num: number): string {
    if (num == null) return '0.00'
    return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
}
</script>

<style scoped>
.book-earnings {
  padding: 16px 24px;
}
.book-earnings h2 {
  margin: 0 0 16px;
}
.block {
  margin-bottom: 16px;
}
.filter-content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}
.filter-item {
  display: flex;
  align-items: center;
  gap: 6px;
}
.filter-item label {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}
.spacer {
  flex: 1 1 auto;
}
.totals {
  font-size: 13px;
  color: #606266;
}
.filter-tip {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
  font-size: 12px;
  color: #909399;
  line-height: 1.9;
}
.money {
  color: #e6a23c;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.dim {
  color: #c0c4cc;
}
.tiny {
  font-size: 11px;
  margin-left: 6px;
}
.no-author {
  color: #f56c6c;
  font-size: 12px;
}
.price-default {
  color: #909399;
}
.detail {
  padding: 12px 24px 12px 48px;
  background: #fafafa;
  min-height: 60px;
}
.detail-error {
  text-align: center;
  color: #f56c6c;
  padding: 16px;
}
.empty-tip {
  text-align: center;
  color: #999;
  padding: 16px;
}
.period-header {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}
.p-label {
  font-weight: 600;
  min-width: 120px;
}
.p-range {
  font-size: 12px;
  color: #c0c4cc;
  min-width: 180px;
}
.p-meta {
  font-size: 13px;
  color: #606266;
}
.empty {
  text-align: center;
  padding: 60px 0;
  color: #999;
}
.empty i {
  font-size: 56px;
  margin-bottom: 16px;
  display: block;
}
.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
}
.hint {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}
.warn {
  color: #e6a23c;
}
.pii-tip {
  margin-bottom: 12px;
}
.author-link {
  color: #409eff;
  cursor: pointer;
}
.author-link:hover {
  text-decoration: underline;
}
.multi-warn {
  color: #f56c6c;
  margin-left: 4px;
  cursor: help;
}
.no-profile {
  color: #e6a23c;
  margin-left: 3px;
  cursor: help;
}
.mono {
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.dlg-hint {
  float: left;
  line-height: 32px;
  font-size: 12px;
  color: #909399;
}
</style>
